import Docxtemplater from 'docxtemplater';
import { toast } from 'react-toastify';
import JSZipUtils from 'jszip-utils';
import moment from 'moment';
import JSZip from 'jszip';

import { fplsFetch, generateFileInfo, hasPerm, isOwner, perms } from '../../shared/utils';
import { setCustomDocumentUploadProps, updateCustomDocumentTemplates } from './reducers';
import { SUBMIT_BUTTON_ID } from '../builder/components/modals/logic-config/utils';
import { fplsConstants } from '../../shared/constants';
import { formAmendProps } from '../../shared/form-lib';
import UiUploader from '../../shared/uploader';
import { cloneDeep } from 'lodash';
import win from '../../shared/win';
import {
  enabledEmailNotificationMetadata,
  enabledSubmissionEditMetadata,
  addedAutoresponderMetadata,
  enabledPaymentTestMode,
} from '../../shared/mixpanel';

const {
  PLAN: {
    allowed_storage_services = [],
    has_document_merge = false,
    custom_document_limit = 0,
    is_platinum = false,
    is_trial = false,
  } = {},
  location: { protocol = '', hostname = '', port = '' } = {},
  ACTIVE_TEAM = null,
} = win || {};

// tabs tracked for activity logging
export const formSettingsTabs = {
  notifications: 'Notifications',
  formTransfer: 'Form transfer',
  customTexts: 'Custom texts',
  submissions: 'Submissions',
  payments: 'Payments',
  general: 'General',
};

// Mixpanel actions tracking
export const mixpanelTrackSetting = (action, data = {}) => {
  if (process.env.NODE_ENV !== 'production') return;

  window.mixpanel.track(action, {
    'Form ID': window.FORM_ID,
    ...data,
  });
};

// Intercom events tracking
export const intercomTrackSetting = (setting, data = {}) => {
  if (process.env.NODE_ENV !== 'production') return;
  const action = `form_settings-${setting}`;
  const formId = window.FORM_ID;
  const formUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}/form/${formId}`;

  const metadata = {
    setting,
    form_id: formId,
    form_url: formUrl,
  };

  // You can only add a maximum of 3 properties in data because Intercom allows 5 metadata max
  Object.entries(data).forEach(([key, value]) => {
    if (value && Object.keys(metadata).length < 5) {
      metadata[key] = value;
    }
  });

  window.Intercom('trackEvent', action, metadata);
};

export const trackFormSettings = (newForm, oldForm) => {
  if (newForm.max_submissions && !oldForm.max_submissions) {
    intercomTrackSetting('submissions_limit');
    mixpanelTrackSetting('Added Submission Limit');
  }

  if (newForm.save_and_continue && !oldForm.save_and_continue) {
    intercomTrackSetting('save_and_continue');
    mixpanelTrackSetting('Enabled Save and Continue');
  }

  if (newForm.get_location && !oldForm.get_location) {
    intercomTrackSetting('location_enabled');
    mixpanelTrackSetting('Enabled Location');
  }

  if (newForm.include_response_in_notification && !oldForm.include_response_in_notification) {
    intercomTrackSetting('response_in_notification');
  }

  if (newForm.autoresponder_message && !oldForm.autoresponder_message) {
    mixpanelTrackSetting('Added Autoresponder', addedAutoresponderMetadata(newForm));
  }

  if (oldForm.notification_type === 'off' && newForm.notification_type !== 'off') {
    intercomTrackSetting('submission_notification_enabled', {
      notification_type: newForm.notification_type,
    });
    mixpanelTrackSetting('Enabled Email Notification', enabledEmailNotificationMetadata(newForm));
  }

  if (oldForm.notification_attachment_format === null && newForm.notification_type !== null) {
    intercomTrackSetting('include_attachment_in_email', {
      attachment_format: newForm.notification_attachment_format,
    });
  }

  if (newForm.options.private) {
    intercomTrackSetting('made_form_private');
    mixpanelTrackSetting('Made Form Private');
  }

  if (!oldForm.options.edit_response && newForm.options.edit_response) {
    mixpanelTrackSetting('Enabled Submission Edit', enabledSubmissionEditMetadata(newForm));
  }

  if (oldForm.options.paypal_live_mode && !newForm.options.paypal_live_mode) {
    mixpanelTrackSetting('Enabled Payment Test Mode', enabledPaymentTestMode('paypal'));
  }

  if (oldForm.options.stripe_live_mode && !newForm.options.stripe_live_mode) {
    mixpanelTrackSetting('Enabled Payment Test Mode', enabledPaymentTestMode('stripe'));
  }

  if (!oldForm.deadline && newForm.deadline) {
    intercomTrackSetting('submission_deadline');
    mixpanelTrackSetting('Added Submission Deadline');
  }
  if (!oldForm.options.edit_deadline && newForm.options.edit_deadline) {
    intercomTrackSetting('submission_deadline');
    mixpanelTrackSetting('Added Edit Deadline');
  }

  if (!oldForm.after_redirect_to && newForm.after_redirect_to) {
    mixpanelTrackSetting('Enabled Form Redirect');
  }

  if (newForm.another_response_link) {
    intercomTrackSetting('enabled_another_response_link');
    mixpanelTrackSetting('Enabled Submit Another Response');
  }

  if (newForm.show_print_response) {
    intercomTrackSetting('enabled_print_response');
    mixpanelTrackSetting('Enabled Print Response');
  }

  if (newForm.storage_service !== oldForm.storage_service) {
    intercomTrackSetting('changed_form_file_storage', {
      new_storage_service: newForm.storage_service,
    });
  }

  if (!newForm.options.stop_edit_after) {
    intercomTrackSetting('stop_edit_after');
  }
};

export const capitalizeTitle = (title) => {
  const exclusions = [
    'a',
    'an',
    'the',
    'and',
    'but',
    'or',
    'nor',
    'for',
    'on',
    'at',
    'to',
    'by',
    'of',
    'in',
    'with',
    'is',
    'any',
  ]; // Common words to exclude
  const words = title.toLowerCase().split(' ');

  return words
    .map((word, index) => {
      // Always capitalize the first and last word
      if (index === 0 || index === words.length - 1 || !exclusions.includes(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word; // Exclude from capitalization
    })
    .join(' ');
};

export const getCondtionalExtraHelpText = (condition, { text, isError = false } = {}) => {
  if (!condition) return '';
  return `<span class=${isError ? 'error' : 'extra-help-text'}>${text}</span>`;
};

export const fileStorageServiceOptions = (() => {
  const storageServices = [
    { text: 'Dropbox', value: 'dropbox', key: 'dropbox' },
    { text: 'Formplus', value: 'formplus', key: 'formplus' },
    { text: 'Google Drive', value: 'google_drive', key: 'google_drive' },
    { text: 'Microsoft OneDrive', value: 'one_drive', key: 'one_drive' },
  ];

  if (!Array.isArray(allowed_storage_services)) return [];
  return storageServices.filter((option) => allowed_storage_services.includes(option.value));
})();

export const formResponsesStorageServiceOptions = [
  { text: 'Formplus', value: 'formplus', key: 'formplus' },
  { text: 'Google Sheets', value: 'google_sheet', key: 'google_sheet' },
  { text: 'Formplus & Google Sheets', value: 'formplus_sheet', key: 'formplus_sheet' },
];

export const timezoneOptions = fplsConstants.timezone.map((timezone) => {
  const zone = {
    id: timezone.id,
    key: timezone.id,
    text: timezone.name,
    value: timezone.zone,
  };
  return zone;
});

export const getClientTimezone = () => {
  let clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  clientTimezone = timezoneOptions.find((timezone) => timezone.key === clientTimezone);
  if (clientTimezone) return clientTimezone.value;
  return timezoneOptions[0].value;
};

export const formClosureOptions = [
  { text: 'Leave open indefinitely (Default)', value: 'always_open', key: 'always_open' },
  { text: 'Close immediately', value: 'closed', key: 'closed' },
  { text: 'Close on submission limit', value: 'max_submissions', key: 'max_submissions' },
  { text: 'Close on specified date', value: 'deadline', key: 'deadline' },
  {
    text: 'Close on submission limit and specified date',
    value: 'deadline_with_max_submissions',
    key: 'deadline_with_max_submissions',
  },
];

export const notificationScheduleOptions = [
  // { text: 'Do not send any notification', value: 'off', key: 'off' },
  { text: 'Send a notification with every response', value: 'submission', key: 'submission' },
  { text: 'Send a summary of notifications everyday', value: 'daily', key: 'daily' },
  { text: 'Send a summary of notifications every week', value: 'weekly', key: 'weekly' },
  { text: 'Send a summary of notifications every month', value: 'monthly', key: 'monthly' },
];

export const weekDayOptions = [
  { text: 'Monday', value: 0, key: 'monday' },
  { text: 'Tuesday', value: 1, key: 'tuesday' },
  { text: 'Wednesday', value: 2, key: 'wednesday' },
  { text: 'Thursday', value: 3, key: 'thursday' },
  { text: 'Friday', value: 4, key: 'friday' },
  { text: 'Saturday', value: 5, key: 'saturday' },
  { text: 'Sunday', value: 6, key: 'sunday' },
];

export const monthDayOptions = Array.from({ length: 31 }, (_, i) => {
  const day = i + 1;
  const suffix =
    day % 10 === 1 && day % 100 !== 11
      ? 'st'
      : day % 10 === 2 && day % 100 !== 12
      ? 'nd'
      : day % 10 === 3 && day % 100 !== 13
      ? 'rd'
      : 'th';

  return {
    text: `${day}${suffix}`,
    key: `${day}${suffix}`,
    value: day,
  };
});

export const notificationEmptyResponseFieldOptions = [
  { text: 'Leave blank', value: '', key: 'blank' },
  { text: 'Show "empty" text', value: 'Empty', key: 'empty' },
  { text: "Don't show questions with empty fields", value: 'Hidden', key: 'hidden' },
];

export const notificationAttachmentFormatOptions = [
  { text: 'Microsoft Word', value: 'docx', key: 'docx' },
  { text: 'PDF', value: 'pdf', key: 'pdf' },
  // { text: "Don't add documents to email", value: '', key: 'none' },
];

export const checkPaymentFields = (form) =>
  form.definition.pages.some((page) => {
    return page.controls.some((control) => ['stripe', 'paypal', 'paystack', 'flutterwave'].includes(control.type));
  });

export const getSyncableOptions = (form) => {
  const logicHasSubmitAction = form.definition.rules.some((rule) =>
    rule.actions.some((action) => action.id === SUBMIT_BUTTON_ID)
  );

  const hasTimer = form.options.enable_timer;
  const hasQuizTimer = hasTimer && form.options.timer_options.timer_purpose === 'quiz';
  const hasPaymentField = checkPaymentFields(form);

  const disableSubmitOptionUse = hasTimer || hasPaymentField;

  let disableSubmitOptionUseText = '';
  if (hasTimer && !hasPaymentField) {
    disableSubmitOptionUseText = 'This feature cannot be used when the form has a timer';
  } else if (!hasTimer && hasPaymentField) {
    disableSubmitOptionUseText = 'This feature cannot be used when form has payment fields';
  } else if (hasTimer && hasPaymentField) {
    disableSubmitOptionUseText = 'This feature cannot be used when the form has a timer and has payment fields';
  }

  return {
    disableSubmitOptionUseText,
    disableSubmitOptionUse,
    logicHasSubmitAction,
    hasQuizTimer,
  };
};

export const isValidGoogleAnalyticsID = (str) => {
  if (str.includes('UA-')) {
    if (str.length < 9 || str.length > 17) return false;
    else if (str.length >= 9 && str.length <= 17) return /^ua-\d{4,9}-\d{1,4}$/i.test(str.toString());
  }

  if (str.includes('G-')) {
    if (str.length < 9) return false;
    else if (str.length >= 9) return /^G-[A-Z0-9]+$/.test(str.toString());
  }
};

export const isActiveTeamOwner = isOwner(ACTIVE_TEAM || {});

export const editAccountPermitted = isActiveTeamOwner || hasPerm(perms.EDIT_ACCOUNT_SETTINGS, ACTIVE_TEAM);

export const manageDocxTemplatePermitted = isActiveTeamOwner || hasPerm(perms.MANAGE_DOCUMENT_TEMPLATES, ACTIVE_TEAM);

export const canUploadCustomTemplates = () => (_, getState) => {
  const {
    customDocuments: { templates },
  } = getState().settings;

  const templateCount = templates.length;

  if (!has_document_merge) {
    return {
      allowed: false,
      message: 'Feature available on the Platinum and Enterprise plan only.',
    };
  }

  if ((is_trial && templateCount >= 3) || (is_platinum && templateCount >= custom_document_limit)) {
    const message = is_trial
      ? 'You have reached the upload limit on your plan. Upgrade to the Platinum plan to upload more document templates.'
      : 'You have reached the upload limit on your plan. Contact support to continue uploading document templates.';

    return {
      allowed: false,
      message,
    };
  }

  return { allowed: true, message: '' };
};

export const getQuestionInternalIdPair = () => (_, getState) => {
  const {
    form: {
      formData: { definition },
    },
  } = getState().settings;

  if (!definition) return;

  const fieldsToRemove = ['add_row', 'media_image', 'embed_field'];
  const fieldsWithTextAsValue = ['description', 'media_header'];
  const formDefinition = formAmendProps(cloneDeep(definition));
  const questionInternalIdPair = [];

  const getQuestions = (ctrl) => {
    const questions = {
      label: !fieldsWithTextAsValue.includes(ctrl.type) ? ctrl.props.label || ctrl.props.text : '',
      prependedText: ctrl.props.prepended_text || '',
      appendedText: ctrl.props.appended_text || '',
      helpText: ctrl.props.subtext || '',
    };

    const questionsWithValue = Object.values(questions).filter(Boolean);
    return questionsWithValue.length ? questionsWithValue.join('\n') : '';
  };

  formDefinition.pages.forEach((page) => {
    page.controls.forEach((ctrl) => {
      if (ctrl.type === 'table_field') {
        // Add table label if available before appending column fields
        const question = getQuestions(ctrl);
        if (question) questionInternalIdPair.push({ question });

        ctrl.props.column_headers.forEach((header, i) => {
          const customInternalId = `${ctrl.internal_id}_${i}`;
          questionInternalIdPair.push({
            question: header,
            answer: `{{ answer_${customInternalId} }}`,
          });
        });
      } else if (!fieldsToRemove.includes(ctrl.type)) {
        const getAnswer = () =>
          fieldsWithTextAsValue.includes(ctrl.type) ? ctrl.props.text : `{{ answer_${ctrl.internal_id} }}`;

        const entry = { answer: getAnswer() };
        const question = getQuestions(ctrl);

        if (question) entry.question = question;
        questionInternalIdPair.push(entry);
      }
    });
  });

  questionInternalIdPair.push({
    question: 'Submission timestamp',
    answer: '{{ submission_timestamp }}',
  });

  return questionInternalIdPair;
};

const generateFileName = (title) => {
  let newName = title.slice(0, 20);
  newName = newName.replace(/[\W_]+/g, '-').toLowerCase();
  return newName;
};

const loadFile = (url, callback) => {
  JSZipUtils.getBinaryContent(url, callback);
};

export const generateCustomDocumentTemplate = () => (dispatch, getState) => {
  const {
    form: { formData },
  } = getState().settings;

  const questionInternalIdPair = dispatch(getQuestionInternalIdPair());

  loadFile('/static/documents/single-response-template.docx', (error, content) => {
    if (error) throw error;

    const zip = new JSZip(content);
    const doc = new Docxtemplater().loadZip(zip);
    doc.setOptions({ paragraphLoop: true });
    doc.setData({
      form_title: '{{ form_title }}',
      response: questionInternalIdPair,
    });

    try {
      doc.render();
    } catch (error) {
      const e = {
        message: error.message,
        name: error.name,
        stack: error.stack,
        properties: error.properties,
      };
      console.error(JSON.stringify({ error: e }));
      throw error;
    }

    const out = doc.getZip().generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const link = document.createElement('a');
    const url = URL.createObjectURL(out);
    link.href = url;
    link.download = `sample-custom-document-template-${generateFileName(formData.name)}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};

export const doDocUpload = (file) => (dispatch, getState) => {
  const {
    customDocuments: { templates },
    form: { id: formId },
  } = getState().settings;

  const uploader = new UiUploader();
  const maxFileSize = 1024 * 1024 * 10;
  const docxmime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  // Start the upload state
  dispatch(
    setCustomDocumentUploadProps({
      isUploadingTemplate: true,
      templateUploadError: '',
    })
  );

  // Validate file type and size
  if (!file || file.type !== docxmime) {
    dispatch(
      setCustomDocumentUploadProps({
        isUploadingTemplate: false,
        templateUploadError: 'Please upload a docx file',
      })
    );
    return;
  }

  // Validate maximum size
  if (file.size > maxFileSize) {
    dispatch(
      setCustomDocumentUploadProps({
        isUploadingTemplate: false,
        templateUploadError: 'Unable to upload file. Maximum file size is 10MB. Please check and try and again.',
      })
    );
    return;
  }

  const reader = new FileReader();
  reader.readAsDataURL(file);

  reader.onload = () => {
    const docxDataURL = reader.result;
    loadFile(docxDataURL, async (error, content) => {
      if (error) throw error;

      const zip = new JSZip(content);
      const doc = new Docxtemplater().loadZip(zip);
      const text = doc.getFullText();

      // the regex is checking for
      // <{{><0++ space/s><word/s><space/s><word/s><0++ space/s><optional }}>
      const invalidContentRegex = /\{\{\s*[A-Za-z0-9_]+\s+[A-Za-z0-9_]+\s*(\}\})?/g;

      const invalidContents = text.match(invalidContentRegex);

      if (invalidContents && invalidContents.length > 0) {
        dispatch(
          setCustomDocumentUploadProps({
            isUploadingTemplate: false,
            templateUploadError:
              invalidContents.length === 1 ? 'Docx file has an invalid tag' : 'Docx file has invalid tags',
          })
        );

        return;
      }

      uploader.addFiles([file]);

      try {
        const response = await fplsFetch({ url: '/api/v2/user/custom-template-upload' });

        if (response) {
          uploader.startUpload({
            headers: { 'Content-Type': docxmime },
            url: response.upload_url,
            concurrency: 1,
            method: 'PUT',
            type: 'File',
            onProgress: (stream) => {
              dispatch(
                setCustomDocumentUploadProps({
                  templateUploadProgress: {
                    loaded: stream.loaded,
                    humanSize: stream.humanSize,
                  },
                })
              );
            },
            onCompleted: async () => {
              const fileInfo = generateFileInfo(response.blob_name, file);

              try {
                const details = await fplsFetch({
                  data: { file_field_id: '', file_info: fileInfo },
                  url: '/api/v2/user/custom-template-upload',
                  // removeToken: true,
                  method: 'POST',
                });

                const { path: templateUrl, filename: fileName } = details;

                const newTemplate = await fplsFetch({
                  data: { fileUrl: templateUrl, fileName: fileName },
                  url: `/api/v2/user/${formId}/custom_templates`,
                  // removeToken: true,
                  method: 'POST',
                });

                if (newTemplate && !newTemplate.error) {
                  toast.success('Template upload successful!');

                  dispatch(
                    setCustomDocumentUploadProps({
                      isUploadingTemplate: false,
                      templateUploadError: '',
                      templateUploadProgress: {
                        loaded: 0,
                        humanSize: 0,
                      },
                    })
                  );

                  dispatch(
                    updateCustomDocumentTemplates({
                      values: {
                        newTemplate,
                      },
                    })
                  );
                }
              } catch (error) {
                console.error('Error while uploading docx template: ', error);

                dispatch(
                  setCustomDocumentUploadProps({
                    isUploadingTemplate: false,
                    templateUploadError:
                      'An error while uploading docx template. ' +
                      'Please refresh & try again. You may also send an email ' +
                      'to support@formpl.us if this persists.',
                    templateUploadProgress: {
                      loaded: 0,
                      humanSize: 0,
                    },
                  })
                );
              }
            },
          });
        }
      } catch (error) {
        console.error('notificationTemplateUrl.get error', error);

        dispatch(
          setCustomDocumentUploadProps({
            isUploadingTemplate: false,
            templateUploadError: 'Upload failed. Please try again or contact support.',
          })
        );
      }
    });
  };
};

export const formatTimestampToSystemTimeZone = (timestamp) => {
  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  let date = new Date(timestamp);

  // If native parsing fails, fall back to moment
  if (isNaN(date.getTime())) {
    const parsed = moment(timestamp, 'HH:mm:ss DD MMM YYYY', true);
    if (!parsed.isValid()) return 'Invalid date';
    date = parsed.toDate();
  }

  const datePart = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: systemTimeZone,
  }).format(date);

  const timePart = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: systemTimeZone,
  }).format(date);

  return `${datePart}, ${timePart}`;
};

export const compareDates = (dateString1, dateString2) => {
  const date1 = new Date(dateString1);
  const date2 = new Date(dateString2);

  // Set seconds and milliseconds to zero for comparison
  date1.setSeconds(0, 0);
  date2.setSeconds(0, 0);

  return date1.getTime() === date2.getTime();
};

export const isWithinRange = (date, start, end) => {
  const timestamp = new Date(date).getTime();
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();

  // Determine the actual range boundaries
  const [minTime, maxTime] = [startTime, endTime].sort((a, b) => a - b);

  return timestamp >= minTime && timestamp <= maxTime;
};

export const formattedDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

export const addOneDay = (dateString) => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  return formattedDate(date);
};

export const getFormattedDateDaysBehind = (dayCount) => {
  // Get the current date
  const currentDate = new Date();

  // Subtract one day (in milliseconds)
  const previousDate = new Date(currentDate);
  previousDate.setDate(currentDate.getDate() - dayCount);

  // Format the date as "Month Day, Year"
  return formattedDate(previousDate);
};

export const isDateInPast = (dateToCheck) => {
  // Get the current date and set it to 11:59 PM
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // Set the dateToCheck to 11:59 PM
  const checkDate = new Date(dateToCheck);
  checkDate.setHours(23, 59, 59, 999);

  // Return true if dateToCheck is behind today's date
  return checkDate < today;
};

export const isInValidTemplateString = (template) => {
  if (!template) return false;

  // Count opening and closing tags
  const openTags = (template.match(/{{/g) || []).length;
  const closeTags = (template.match(/}}/g) || []).length;

  // Check for balanced braces
  if (openTags !== closeTags) return true;

  // Use a regular expression to find all Jinja2-style expressions
  const templateExpressions = template.match(/{{(.*?)}}/g) || [];

  // Check each expression for proper formatting
  for (const expr of templateExpressions) {
    // Remove surrounding {{ and }} for the expression content
    const content = expr.slice(2, -2).trim();

    // Check for nested braces within the expression content
    if (content.includes('{{') || content.includes('}}')) return true;
  }

  return false;
};

export const extraAutoresponderConfigTemplate = {
  feature_available: true,
  include_response: false,
  default_email: '',
  replyto_email: '',
  sender_email: '',
  display_name: '',
  invalid: true,
  field_id: '',
  message: '',
  title: '',
  id: '',
};

export const eSignInfoBullets = [
  'Automatically turning the signed form into PDF documents',
  'Require form users to give consent to their signatures',
  'Receive an audit trail for each signed document',
  'Disable submission editing',
];

export const currencyOptions = [
  { value: 'AFN', text: 'Afghan Afghani', key: 'AFN' },
  { value: 'ALL', text: 'Albanian Lek', key: 'ALL' },
  { value: 'DZD', text: 'Algerian Dinar', key: 'DZD' },
  { value: 'AOA', text: 'Angolan Kwanza', key: 'AOA' },
  { value: 'ARS', text: 'Argentine Peso', key: 'ARS' },
  { value: 'AMD', text: 'Armenian Dram', key: 'AMD' },
  { value: 'AWG', text: 'Aruban Florin', key: 'AWG' },
  { value: 'AUD', text: 'Australian Dollar', key: 'AUD' },
  { value: 'AZN', text: 'Azerbaijani Manat', key: 'AZN' },
  { value: 'BSD', text: 'Bahamian Dollar', key: 'BSD' },
  { value: 'BHD', text: 'Bahraini Dinar', key: 'BHD' },
  { value: 'BDT', text: 'Bangladeshi Taka', key: 'BDT' },
  { value: 'BBD', text: 'Barbadian Dollar', key: 'BBD' },
  { value: 'BYN', text: 'Belarusian Ruble', key: 'BYN' },
  { value: 'BZD', text: 'Belize Dollar', key: 'BZD' },
  { value: 'XOF', text: 'CFA Franc BCEAO', key: 'XOF' },
  { value: 'BMD', text: 'Bermudian Dollar', key: 'BMD' },
  { value: 'BTN', text: 'Bhutanese Ngultrum', key: 'BTN' },
  { value: 'BOB', text: 'Bolivian Boliviano', key: 'BOB' },
  { value: 'BAM', text: 'Bosnia-Herzegovina Convertible Mark', key: 'BAM' },
  { value: 'BWP', text: 'Botswana Pula', key: 'BWP' },
  { value: 'BRL', text: 'Brazilian Real', key: 'BRL' },
  { value: 'GBP', text: 'British Pound Sterling', key: 'GBP' },
  { value: 'BND', text: 'Brunei Dollar', key: 'BND' },
  { value: 'BGN', text: 'Bulgarian Lev', key: 'BGN' },
  { value: 'KHR', text: 'Cambodian Riel', key: 'KHR' },
  { value: 'CAD', text: 'Canadian Dollar', key: 'CAD' },
  { value: 'CVE', text: 'Cape Verdean Escudo', key: 'CVE' },
  { value: 'KYD', text: 'Cayman Islands Dollar', key: 'KYD' },
  { value: 'XAF', text: 'CFA Franc BEAC', key: 'XAF' },
  { value: 'XPF', text: 'CFP Franc', key: 'XPF' },
  { value: 'CLP', text: 'Chilean Peso', key: 'CLP' },
  { value: 'CNY', text: 'Chinese Yuan', key: 'CNY' },
  { value: 'COP', text: 'Colombian Peso', key: 'COP' },
  { value: 'KMF', text: 'Comorian Franc', key: 'KMF' },
  { value: 'CDF', text: 'Congolese Franc', key: 'CDF' },
  { value: 'CRC', text: 'Costa Rican Colón', key: 'CRC' },
  { value: 'HRK', text: 'Croatian Kuna', key: 'HRK' },
  { value: 'CUP', text: 'Cuban Peso', key: 'CUP' },
  { value: 'CUC', text: 'Cuban Convertible Peso', key: 'CUC' },
  { value: 'CZK', text: 'Czech Koruna', key: 'CZK' },
  { value: 'DKK', text: 'Danish Krone', key: 'DKK' },
  { value: 'DJF', text: 'Djiboutian Franc', key: 'DJF' },
  { value: 'DOP', text: 'Dominican Peso', key: 'DOP' },
  { value: 'EGP', text: 'Egyptian Pound', key: 'EGP' },
  { value: 'ERN', text: 'Eritrean Nakfa', key: 'ERN' },
  { value: 'ETB', text: 'Ethiopian Birr', key: 'ETB' },
  { value: 'EUR', text: 'Euro', key: 'EUR' },
  { value: 'FKP', text: 'Falkland Islands Pound', key: 'FKP' },
  { value: 'FJD', text: 'Fijian Dollar', key: 'FJD' },
  { value: 'GMD', text: 'Gambian Dalasi', key: 'GMD' },
  { value: 'GEL', text: 'Georgian Lari', key: 'GEL' },
  { value: 'GHS', text: 'Ghanaian Cedi', key: 'GHS' },
  { value: 'GIP', text: 'Gibraltar Pound', key: 'GIP' },
  { value: 'GTQ', text: 'Guatemalan Quetzal', key: 'GTQ' },
  { value: 'GNF', text: 'Guinean Franc', key: 'GNF' },
  { value: 'GYD', text: 'Guyanese Dollar', key: 'GYD' },
  { value: 'HKD', text: 'Hong Kong Dollar', key: 'HKD' },
  { value: 'HUF', text: 'Hungarian Forint', key: 'HUF' },
  { value: 'ISK', text: 'Icelandic Króna', key: 'ISK' },
  { value: 'INR', text: 'Indian Rupee', key: 'INR' },
  { value: 'IDR', text: 'Indonesian Rupiah', key: 'IDR' },
  { value: 'IRR', text: 'Iranian Rial', key: 'IRR' },
  { value: 'IQD', text: 'Iraqi Dinar', key: 'IQD' },
  { value: 'ILS', text: 'Israeli New Shekel', key: 'ILS' },
  { value: 'JMD', text: 'Jamaican Dollar', key: 'JMD' },
  { value: 'JPY', text: 'Japanese Yen', key: 'JPY' },
  { value: 'JOD', text: 'Jordanian Dinar', key: 'JOD' },
  { value: 'KZT', text: 'Kazakhstani Tenge', key: 'KZT' },
  { value: 'KES', text: 'Kenyan Shilling', key: 'KES' },
  { value: 'KWD', text: 'Kuwaiti Dinar', key: 'KWD' },
  { value: 'KGS', text: 'Kyrgyzstani Som', key: 'KGS' },
  { value: 'LAK', text: 'Lao Kip', key: 'LAK' },
  { value: 'LBP', text: 'Lebanese Pound', key: 'LBP' },
  { value: 'LSL', text: 'Lesotho Loti', key: 'LSL' },
  { value: 'LRD', text: 'Liberian Dollar', key: 'LRD' },
  { value: 'LYD', text: 'Libyan Dinar', key: 'LYD' },
  { value: 'MOP', text: 'Macanese Pataca', key: 'MOP' },
  { value: 'MKD', text: 'Macedonian Denar', key: 'MKD' },
  { value: 'MGA', text: 'Malagasy Ariary', key: 'MGA' },
  { value: 'MWK', text: 'Malawian Kwacha', key: 'MWK' },
  { value: 'MYR', text: 'Malaysian Ringgit', key: 'MYR' },
  { value: 'MVR', text: 'Maldivian Rufiyaa', key: 'MVR' },
  { value: 'MRU', text: 'Mauritanian Ouguiya', key: 'MRU' },
  { value: 'MUR', text: 'Mauritian Rupee', key: 'MUR' },
  { value: 'MXN', text: 'Mexican Peso', key: 'MXN' },
  { value: 'MDL', text: 'Moldovan Leu', key: 'MDL' },
  { value: 'MNT', text: 'Mongolian Tögrög', key: 'MNT' },
  { value: 'MAD', text: 'Moroccan Dirham', key: 'MAD' },
  { value: 'MZN', text: 'Mozambican Metical', key: 'MZN' },
  { value: 'NAD', text: 'Namibian Dollar', key: 'NAD' },
  { value: 'NPR', text: 'Nepalese Rupee', key: 'NPR' },
  { value: 'ANG', text: 'Netherlands Antillean Guilder', key: 'ANG' },
  { value: 'NZD', text: 'New Zealand Dollar', key: 'NZD' },
  { value: 'NIO', text: 'Nicaraguan Córdoba', key: 'NIO' },
  { value: 'NGN', text: 'Nigerian Naira', key: 'NGN' },
  { value: 'NOK', text: 'Norwegian Krone', key: 'NOK' },
  { value: 'OMR', text: 'Omani Rial', key: 'OMR' },
  { value: 'PKR', text: 'Pakistani Rupee', key: 'PKR' },
  { value: 'PAB', text: 'Panamanian Balboa', key: 'PAB' },
  { value: 'PYG', text: 'Paraguayan Guaraní', key: 'PYG' },
  { value: 'PEN', text: 'Peruvian Sol', key: 'PEN' },
  { value: 'PHP', text: 'Philippine Peso', key: 'PHP' },
  { value: 'PLN', text: 'Polish Zloty', key: 'PLN' },
  { value: 'QAR', text: 'Qatari Rial', key: 'QAR' },
  { value: 'RON', text: 'Romanian Leu', key: 'RON' },
  { value: 'RUB', text: 'Russian Ruble', key: 'RUB' },
  { value: 'RWF', text: 'Rwandan Franc', key: 'RWF' },
  { value: 'SVC', text: 'Salvadoran Colón', key: 'SVC' },
  { value: 'WST', text: 'Samoan Tala', key: 'WST' },
  { value: 'SAR', text: 'Saudi Riyal', key: 'SAR' },
  { value: 'RSD', text: 'Serbian Dinar', key: 'RSD' },
  { value: 'SCR', text: 'Seychellois Rupee', key: 'SCR' },
  { value: 'SGD', text: 'Singapore Dollar', key: 'SGD' },
  { value: 'SLL', text: 'Sierra Leonean Leone', key: 'SLL' },
  { value: 'SOS', text: 'Somali Shilling', key: 'SOS' },
  { value: 'ZAR', text: 'South African Rand', key: 'ZAR' },
  { value: 'KRW', text: 'South Korean Won', key: 'KRW' },
  { value: 'LKR', text: 'Sri Lankan Rupee', key: 'LKR' },
  { value: 'SDG', text: 'Sudanese Pound', key: 'SDG' },
  { value: 'SRD', text: 'Surinamese Dollar', key: 'SRD' },
  { value: 'SEK', text: 'Swedish Krona', key: 'SEK' },
  { value: 'CHF', text: 'Swiss Franc', key: 'CHF' },
  { value: 'SYP', text: 'Syrian Pound', key: 'SYP' },
  { value: 'TWD', text: 'New Taiwan Dollar', key: 'TWD' },
  { value: 'TZS', text: 'Tanzanian Shilling', key: 'TZS' },
  { value: 'THB', text: 'Thai Baht', key: 'THB' },
  { value: 'TOP', text: 'Tongan Paʻanga', key: 'TOP' },
  { value: 'TTD', text: 'Trinidad and Tobago Dollar', key: 'TTD' },
  { value: 'TND', text: 'Tunisian Dinar', key: 'TND' },
  { value: 'TRY', text: 'Turkish Lira', key: 'TRY' },
  { value: 'TMT', text: 'Turkmenistani Manat', key: 'TMT' },
  { value: 'USD', text: 'United States Dollar', key: 'USD' },
  { value: 'UGX', text: 'Ugandan Shilling', key: 'UGX' },
  { value: 'UAH', text: 'Ukrainian Hryvnia', key: 'UAH' },
  { value: 'AED', text: 'United Arab Emirates Dirham', key: 'AED' },
  { value: 'UYU', text: 'Uruguayan Peso', key: 'UYU' },
  { value: 'UZS', text: 'Uzbekistani Som', key: 'UZS' },
  { value: 'VUV', text: 'Vanuatu Vatu', key: 'VUV' },
  { value: 'VES', text: 'Venezuelan Bolívar Soberano', key: 'VES' },
  { value: 'VND', text: 'Vietnamese Dong', key: 'VND' },
  { value: 'YER', text: 'Yemeni Rial', key: 'YER' },
  { value: 'ZMW', text: 'Zambian Kwacha', key: 'ZMW' },
  { value: 'ZWL', text: 'Zimbabwean Dollar', key: 'ZWL' },
];

export const defaultFormCustomTexts = [
  {
    id: 'buttons',
    title: 'Buttons',
    items: [
      {
        id: 'start_form',
        name: 'start_form',
        label: 'Start:',
        defaultValue: 'Start',
        placeholder: 'Start',
        toolTipText: 'Button text to start form when first viewing the intro page',
      },
      {
        id: 'view_intro_page',
        name: 'view_intro_page',
        label: 'View intro page:',
        defaultValue: 'View intro page',
        placeholder: 'View intro page',
        toolTipText: 'Button text to review an intro page',
      },
      {
        id: 'continue_to_form',
        name: 'continue_to_form',
        label: 'Continue to form:',
        defaultValue: 'Continue to form',
        placeholder: 'Continue to form',
        toolTipText: 'Button text to continue to form when reviewing the intro page',
      },
      {
        id: 'add_new_row',
        name: 'add_new_row',
        label: 'Add new row:',
        defaultValue: 'Add new row',
        placeholder: 'Add new row',
        toolTipText: 'Button text to add a new row for a Table Field',
      },
      {
        id: 'accept',
        name: 'accept',
        label: 'Accept:',
        defaultValue: 'Accept',
        placeholder: 'Accept',
        toolTipText: 'Button text to accept a valid signature for a Signature Field',
      },
      {
        id: 'resets',
        name: 'resets',
        label: 'Reset:',
        defaultValue: 'Reset',
        placeholder: 'Reset',
        toolTipText: 'Button text to reset a Signature Field',
      },
      {
        id: 'print_response_text',
        name: 'print_response_text',
        label: 'Print submitted response:',
        defaultValue: 'Print submitted response',
        placeholder: 'Print submitted response',
        toolTipText: 'Button text displayed to respondents to print a submitted response',
      },
      {
        id: 'submit_another_response',
        name: 'submit_another_response',
        label: 'Submit another response:',
        defaultValue: 'Submit another response',
        placeholder: 'Submit another response',
        toolTipText: 'Button text displayed to respondents to submit another response',
      },
      {
        id: 'continue_text',
        name: 'continue_text',
        label: 'Continue:',
        defaultValue: 'Continue',
        placeholder: 'Continue',
        toolTipText: 'Button text displayed on a save and continue modal to proceed to the next step',
      },
      {
        id: 'next_page',
        name: 'next_page',
        label: 'Next page:',
        defaultValue: 'Next page',
        placeholder: 'Next page',
        toolTipText: 'Button text to move to a another page',
      },
      {
        id: 'previous_page',
        name: 'previous_page',
        label: 'Previous page:',
        defaultValue: 'Previous page',
        placeholder: 'Previous page',
        toolTipText: 'Button text to move to the previous page',
      },
      {
        id: 'submit_text',
        name: 'submit_text',
        label: 'Submit:',
        defaultValue: 'Submit',
        placeholder: 'Submit',
        toolTipText: 'Button text to submit form',
      },
      {
        id: 'edit_submit_text',
        name: 'edit_submit_text',
        label: 'Update response:',
        defaultValue: 'Update response',
        placeholder: 'Update response',
        toolTipText: 'Button text to submit form when editing a response',
      },
      {
        id: 'done',
        name: 'done',
        label: 'Done:',
        defaultValue: 'Done',
        placeholder: 'Done',
        toolTipText: 'Single Select and Multiple Select button',
      },
      {
        id: 'select_all',
        name: 'select_all',
        label: 'Select all:',
        defaultValue: 'Select all',
        placeholder: 'Select all',
        toolTipText: 'Multiple Select button',
      },
      {
        id: 'select_none',
        name: 'select_none',
        label: 'Select none:',
        defaultValue: 'Select none',
        placeholder: 'Select none',
        toolTipText: 'Multiple Select button',
      },
    ],
  },
  {
    id: 'builder-fields',
    title: 'Builder/Fields',
    items: [
      {
        id: 'upload_file',
        name: 'upload_file',
        label: 'Upload File:',
        defaultValue: 'Upload File',
        placeholder: 'Upload File',
        toolTipText: 'The text displayed on a file upload button/field',
      },
      {
        id: 'select_option',
        name: 'select_option',
        label: 'Select Option:',
        defaultValue: 'Select Option',
        placeholder: 'Select Option',
        toolTipText: 'The default text displayed on a Single Select or Multiple Select field',
      },
      {
        id: 'filter_label',
        name: 'filter_label',
        label: 'Filter:',
        defaultValue: 'Filter',
        placeholder: 'Filter',
        toolTipText: 'Message shown in single select and multiple select fields for users to search',
      },
    ],
  },
  {
    id: 'informational-messages',
    title: 'Informational Messages',
    items: [
      {
        id: 'collect_email_text',
        name: 'collect_email_text',
        label: 'Email collection text:',
        defaultValue: 'Please provide your email',
        placeholder: 'Please provide your email',
        toolTipText: `Text shown on the intro page when Autoresponder is enabled on a form and an email field is not selected as recipent's email`,
      },
      {
        id: 'save_form_progress',
        name: 'save_form_progress',
        label: 'Save form progress:',
        defaultValue: 'Save form progress',
        placeholder: 'Save form progress',
        toolTipText: `Header text for the ‘Save and Continue’ modal`,
      },
      {
        id: 'email_for_link',
        name: 'email_for_link',
        label: 'Give your email to get a link to continue filling your form later:',
        defaultValue: 'Give your email to get a link to continue filling your form later',
        placeholder: 'Give your email to get a link to continue filling your form later',
        toolTipText: 'The text displayed to users to input an email to receive a save and continue link',
      },
      {
        id: 'use_saveandContinue_link',
        name: 'use_saveandContinue_link',
        label: 'Use the link below to continue form submission at any time:',
        defaultValue: 'Use the link below to continue form submission at any time',
        placeholder: 'Use the link below to continue form submission at any time',
        toolTipText: 'The text displayed to respondents to copy a saved form link',
      },
      {
        id: 'upload_in_progress',
        name: 'upload_in_progress',
        label: 'Upload in progress:',
        defaultValue: 'Upload in progress',
        placeholder: 'Upload in progress',
        toolTipText: 'Header text displayed to users when they try to submit a form while files are still uploading',
      },
      {
        id: 'wait_for_upload',
        name: 'wait_for_upload',
        label: 'Form submission will be possible when uploaded files are done:',
        defaultValue: 'Form submission will be possible when uploaded files are done',
        placeholder: 'Form submission will be possible when uploaded files are done',
        toolTipText:
          'Additional text displayed to users when they try to submit a form while files are still uploading',
      },
      {
        id: 'loading_form',
        name: 'loading_form',
        label: 'Loading form:',
        defaultValue: 'Loading form',
        placeholder: 'Loading form',
        toolTipText: 'Text displayed to users when form is still loading',
      },
      {
        id: 'wait_for_submit',
        name: 'wait_for_submit',
        label: 'Submitting form. Please wait:',
        defaultValue: 'Submitting form. Please wait',
        placeholder: 'Submitting form. Please wait',
        toolTipText: 'Text displayed to users when form submission is in progress',
      },
      {
        id: 'edit_response',
        name: 'edit_response',
        label: 'Edit response:',
        defaultValue: 'Edit response',
        placeholder: 'Edit response',
        toolTipText: 'Text displayed to users after submission when edit response is switched on',
      },
      {
        id: 'copy_edit_link_text',
        name: 'copy_edit_link_text',
        label: 'Copy edit link text:',
        defaultValue: 'Copy edit link',
        placeholder: 'Copy edit link',
        toolTipText: 'Text displayed on copy button after editing a response',
      },
      {
        id: 'email_for_saved',
        name: 'email_for_saved',
        label: 'An email has also been sent to you containing the link:',
        defaultValue: 'An email has also been sent to you containing the link',
        placeholder: 'An email has also been sent to you containing the link.',
        toolTipText: 'Message shown to users in save and continue pop-up after saving their response',
      },
      {
        id: 'pages_left',
        name: 'pages_left',
        label: 'Remaining form pages text:',
        defaultValue: 'pages(s) left',
        placeholder: 'pages(s) left',
        toolTipText: 'Message shown to respondents on a multipage form to indicate number of pages left',
      },
    ],
  },
  {
    id: 'error-messages',
    title: 'Error Messages',
    items: [
      {
        id: 'deadline_error',
        name: 'deadline_error',
        label: 'Deadline for responses has passed:',
        defaultValue: 'Deadline for responses has passed',
        placeholder: 'Deadline for responses has passed',
        toolTipText: 'Error displayed when the submission deadline for a form has been reached',
      },
      {
        id: 'response_exceeded',
        name: 'response_exceeded',
        label: 'Maximum number of submissions has been reached:',
        defaultValue: 'Maximum number of submissions has been reached',
        placeholder: 'Maximum number of submissions has been reached',
        toolTipText: 'Error displayed when the specified maximum number of submissions has been reached',
      },
      {
        id: 'edit_link_expired',
        name: 'edit_link_expired',
        label: 'This edit link has expired:',
        defaultValue: 'This edit link has expired',
        placeholder: 'This edit link has expired',
        toolTipText: 'This edit link has expired',
      },
      {
        id: 'required_field',
        name: 'required_field',
        label: 'Value for this field is required:',
        defaultValue: 'Value for this field is required',
        placeholder: 'Value for this field is required',
        toolTipText: 'Error displayed when a user does not fill in a required field',
      },
      {
        id: 'invalid_email',
        name: 'invalid_email',
        label: 'Provided text is not a valid email:',
        defaultValue: 'Provided text is not a valid email',
        placeholder: 'Provided text is not a valid email',
        toolTipText: 'Error displayed when a user fills in an invalid email address in an Email field',
      },
      {
        id: 'invalid_number',
        name: 'invalid_number',
        label: 'Provided text is not a number:',
        defaultValue: 'Provided text is not a number',
        placeholder: 'Provided text is not a number',
        toolTipText: 'Error displayed when a user fills in an invalid number in an Number field',
      },
      {
        id: 'invalid_website',
        name: 'invalid_website',
        label: 'Please enter a valid website address. It should start with http://, https://, or www:',
        defaultValue: 'Please enter a valid website address. It should start with http://, https://, or www.',
        placeholder: 'Please enter a valid website address. It should start with http://, https://, or www.',
        toolTipText: 'Error displayed when a user fills in an invalid website address',
      },
      {
        id: 'invalid_phone_no',
        name: 'invalid_phone_no',
        label: 'Provided text is not a valid phone number:',
        defaultValue: 'Provided text is not a valid phone number',
        placeholder: 'Provided text is not a valid phone number',
        toolTipText: 'Error displayed when a user fills in an invalid number in a Phone number field',
      },
      {
        id: 'min_characters',
        name: 'min_characters',
        label: 'Please enter at least {{ minimum }} characters:',
        defaultValue: 'Please enter at least {{ minimum }} characters',
        placeholder: 'Please enter at least {{ minimum }} characters',
        toolTipText: 'Error displayed when a user enters less than the minimum characters set for a text field',
      },
      {
        id: 'max_characters',
        name: 'max_characters',
        label: 'Please enter no more than {{ maximum }} characters:',
        defaultValue: 'Please enter no more than {{ maximum }} characters',
        placeholder: 'Please enter no more than {{ maximum }} characters',
        toolTipText: 'Error displayed when a user enters more than the maximum characters set for a text field',
      },
      {
        id: 'min_selects',
        name: 'min_selects',
        label: 'Please select at least {{ minimum }} options. {{ number }} selected:',
        defaultValue: 'Please select at least {{ minimum }} options. {{ number }} selected',
        placeholder: 'Please select at least {{ minimum }} options. {{ number }} selected',
        toolTipText:
          'Error displayed when a user selects less than the stated minimum number of selections for checkboxes',
      },
      {
        id: 'max_selects',
        name: 'max_selects',
        label: 'Please select at most {{ maximum }} options. {{ number }} selected:',
        defaultValue: 'Please select at most {{ maximum }} options. {{ number }} selected',
        placeholder: 'Please select at most {{ maximum }} options. {{ number }} selected',
        toolTipText:
          'Error displayed when a user selects more than the stated maximum number of selections for checkboxes',
      },
      {
        id: 'min_words',
        name: 'min_words',
        label: 'Please enter at least {{ minimum }} words:',
        defaultValue: 'Please enter at least {{ minimum }} words',
        placeholder: 'Please enter at least {{ minimum }} words',
        toolTipText:
          'Error displayed when a user inputs less than the minimum number of words set for the long text field',
      },
      {
        id: 'max_words',
        name: 'max_words',
        label: 'Please enter no more than {{ maximum }} words:',
        defaultValue: 'Please enter no more than {{ maximum }} words',
        placeholder: 'Please enter no more than {{ maximum }} words',
        toolTipText:
          'Error displayed when a user inputs more than the maximum number of words set for the long text field',
      },
      {
        id: 'signature_upload_error',
        name: 'signature_upload_error',
        label: 'Signature Upload is in progress:',
        defaultValue: 'Signature Upload is in progress',
        placeholder: 'Signature Upload is in progress',
        toolTipText: 'Error displayed when user tries to submit when signature is still uploading',
      },
      {
        id: 'signature_progress_error',
        name: 'signature_progress_error',
        label: 'An error occurred while trying to upload signature, please try again:',
        defaultValue: 'An error occurred while trying to upload signature, please try again',
        placeholder: 'An error occurred while trying to upload signature, please try again',
        toolTipText: 'Error shown if a problem occurs while trying to upload a signature',
      },
      {
        id: 'signature_acceptance_error',
        name: 'signature_acceptance_error',
        label: 'Signatures drawn should be accepted or reset:',
        defaultValue: 'Signatures drawn should be accepted or reset',
        placeholder: 'Signatures drawn should be accepted or reset',
        toolTipText: 'Error shown if a user inputs a signature but does not click the Accept or Reset button',
      },
      {
        id: 'upload_size_error',
        name: 'upload_size_error',
        label: 'Unable to upload:',
        defaultValue: 'Unable to upload',
        placeholder: 'Unable to upload',
        toolTipText: 'Error displayed when a user uploads a file greater than the maximum size',
      },
      {
        id: 'file_error',
        name: 'file_error',
        label: 'Invalid file type selected:',
        defaultValue: 'Invalid file type selected',
        placeholder: 'Invalid file type selected',
        toolTipText:
          'Error displayed when a user uploads a different file type than specified in the selected categories',
      },
      {
        id: 'captcha_error',
        name: 'captcha_error',
        label: 'Please resolve the captcha before submitting the form:',
        defaultValue: 'Please resolve the captcha before submitting the form',
        placeholder: 'Please resolve the captcha before submitting the form',
        toolTipText: 'Error displayed when user tries to submit a form with captcha enabled without resolving captcha',
      },
      {
        id: 'resolve_captcha',
        name: 'resolve_captcha',
        label: 'Resolve captcha:',
        defaultValue: 'Resolve captcha',
        placeholder: 'Resolve captcha',
        toolTipText:
          'Header text displayed when user tries to submit a form with captcha enabled without resolving captcha',
      },
      {
        id: 'incomplete_form',
        name: 'incomplete_form',
        label: `Incomplete form:`,
        defaultValue: `Incomplete form`,
        placeholder: `Incomplete form`,
        toolTipText:
          'Header text displayed within a form when user tries to submit a multi-page form without filling in all required field',
      },
      {
        id: 'form_submission_error',
        name: 'form_submission_error',
        label: 'Form submission error:',
        defaultValue: 'Form submission error',
        placeholder: 'Form submission error',
        toolTipText: 'Header text displayed within a form when there is an error during form submission',
      },
      {
        id: 'empty_form',
        name: 'empty_form',
        label: `You can't save an empty form. Please fill the form fields and then submit:`,
        defaultValue: `You can't save an empty form. Please fill the form fields and then submit.`,
        placeholder: `You can't save an empty form. Please fill the form fields and then submit.`,
        toolTipText: 'Error displayed when a user tries to submit a form without filling in any fields',
      },
      {
        id: 'submit_error',
        name: 'submit_error',
        label: 'Error has occurred when submitting form:',
        defaultValue: 'Error has occurred when submitting form',
        placeholder: 'Error has occurred when submitting form',
        toolTipText: 'Error displayed within a form if there is a problem with form submission',
      },
      {
        id: 'required_provided_error',
        name: 'required_provided_error',
        label: 'Please provide data for all required fields and correct already provided values:',
        defaultValue: 'Please provide data for all required fields and correct already provided values',
        placeholder: 'Please provide data for all required fields and correct already provided values',
        toolTipText:
          'Error displayed when a user tries to submit the form but has not filled in all required fields and some fields have errors',
      },
      {
        id: 'required_error',
        name: 'required_error',
        label: 'Please provide data for all required fields:',
        defaultValue: 'Please provide data for all required fields',
        placeholder: 'Please provide data for all required fields',
        toolTipText: 'Error displayed when a user tries to submit the form but has not filled in all required fields',
      },
      {
        id: 'provided_error',
        name: 'provided_error',
        label: 'Please correct provided data:',
        defaultValue: 'Please correct provided data',
        placeholder: 'Please correct provided data',
        toolTipText: 'Error displayed when a user tries to submit the form but there are fields with errors',
      },
      {
        id: 'loading_error',
        name: 'loading_error',
        label: 'Error loading form:',
        defaultValue: 'Error loading form',
        placeholder: 'Error loading form',
        toolTipText: 'Header text displayed when there is a problem with loading a form',
      },
      {
        id: 'post_submit_error',
        name: 'post_submit_error',
        label: 'Error submitting form:',
        defaultValue: 'Error submitting form',
        placeholder: 'Error submitting form',
        toolTipText: 'Header text displayed on post submission page if there is a problem with form submission',
      },
      {
        id: 'more_post_submit_error',
        name: 'more_post_submit_error',
        label:
          'We are sorry but there was an issue while submitting this form. Please click the button below to view the form and retry using your previous data. You may also reload the form and try again:',
        defaultValue:
          'We are sorry but there was an issue while submitting this form. Please click the button below to view the form and retry using your previous data. You may also reload the form and try again.',
        placeholder:
          'We are sorry but there was an issue while submitting this form. Please click the button below  to view the form and retry using your previous data.  You may also reload the form and try again.',
        toolTipText: 'Additional message displayed on post submission page if there is a problem with form submission',
      },
    ],
  },
];
